import { type HTMLAttributes, useState, useEffect, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import { getSettings } from '@wordpress/date';
import { DayPicker } from 'react-day-picker';
import type {
	Mode,
	CalendarMonth,
	DateRange,
	DayEventHandler,
	DayPickerProps,
	Locale,
} from 'react-day-picker';
import { format, addDays, differenceInCalendarDays } from 'date-fns';
import { formatFullDate } from '../utils/date-time';
import { getDayPickerLocale, getDefaultLocale } from '../utils/locale';
import { useMonthAvailabilityQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	useService,
	useIsDateRange,
	useSelectedDate,
} from '../hooks/useBookingComputed';
import { useStartingMonth } from '../hooks/useStartingMonth';
import { CalendarSkeleton } from './CalendarSkeleton';
import classnames from 'classnames';
import './DateRangePicker.css';

export function DateRangePicker() {
	const { data: service } = useService();
	const isDateRange = useIsDateRange();

	// Client state from store
	const selectedDate = useSelectedDate();
	const selectedRange = useBookingStore((state) => state.selectedRange);
	const originalRange = useBookingStore((state) => state.originalRange);
	const setSelectedDate = useBookingStore((state) => state.setSelectedDate);
	const setSelectedRange = useBookingStore((state) => state.setSelectedRange);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const resourceMode = useBookingStore((state) => state.resourceMode);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);
	const isEntryEdit = useBookingStore((state) => state.isEntryEdit);

	// Starting month logic (reads from store)
	const {
		currentMonth,
		onMonthChange,
		loading: startingMonthLoading,
	} = useStartingMonth();
	const [hasUserNavigated, setHasUserNavigated] = useState(false);

	// If we already have a selected date/range (e.g. from default value or prepopulation),
	// ensure the calendar opens on that month instead of the generic starting month.
	useEffect(() => {
		if (hasUserNavigated) {
			return;
		}

		const selectedStart = selectedRange?.from;
		if (!selectedStart) {
			return;
		}

		const selectedMonthStart = new Date(
			selectedStart.getFullYear(),
			selectedStart.getMonth(),
			1
		);

		const isAlreadyOnSelectedMonth =
			currentMonth.getFullYear() === selectedMonthStart.getFullYear() &&
			currentMonth.getMonth() === selectedMonthStart.getMonth();

		if (!isAlreadyOnSelectedMonth) {
			onMonthChange(selectedMonthStart);
		}
	}, [selectedRange, currentMonth, onMonthChange, hasUserNavigated]);

	const onUserMonthChange = useCallback(
		(month: Date) => {
			setHasUserNavigated(true);
			onMonthChange(month);
		},
		[onMonthChange]
	);

	/**
	 * Filter the current date used by GP Bookings frontend date logic (including the date picker).
	 *
	 * Useful for testing or overriding "today" at runtime.
	 *
	 * This filter replaces the deprecated `gp_bookings_current_date` filter,
	 * which is still supported for backwards compatibility.
	 *
	 * @param {Date} currentDate - The current JavaScript Date object.
	 *
	 * @example Override "Today" for testing
	 * <code>
	 * window.gform.addFilter('gpb_current_date', function(currentDate) {
	 *     return new Date('2026-01-15T00:00:00');
	 * });
	 * </code>
	 */
	// Get today at midnight - respects gpb_current_date filter for testing
	const today = (() => {
		const todayDate = window.gform?.applyFilters
			? window.gform.applyFilters(
					'gpb_current_date',
					window.gform.applyFilters(
						'gp_bookings_current_date',
						new Date()
					)
				)
			: new Date();

		const result = new Date(todayDate);
		result.setHours(0, 0, 0, 0);
		return result;
	})();

	// Fetch month availability
	const { data: monthAvailabilityData, isLoading: monthLoading } =
		useMonthAvailabilityQuery(
			{
				serviceId: currentServiceId,
				year: currentMonth.getFullYear(),
				month: currentMonth.getMonth() + 1,
				resources: selectedResourceIds,
				resourceMode,
				excludeBookingId: excludeBookingId || undefined,
			},
			{
				enabled: currentServiceId > 0,
			}
		);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const monthData = monthAvailabilityData?.days ?? {};
	const loading = startingMonthLoading || monthLoading;
	const selectedCalendarDays =
		selectedRange?.from && selectedRange?.to
			? Math.max(
					0,
					differenceInCalendarDays(
						selectedRange.to,
						selectedRange.from
					)
				)
			: 0;

	// Load locale dynamically based on WordPress settings
	const [locale, setLocale] = useState<Locale | null>(null);
	const [localeLoading, setLocaleLoading] = useState(true);

	useEffect(() => {
		setLocaleLoading(true);
		getDayPickerLocale()
			.then((loadedLocale) => {
				setLocale(loadedLocale);
				setLocaleLoading(false);
			})
			.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Failed to load locale:', error);
				setLocale(getDefaultLocale());
				setLocaleLoading(false);
			});
	}, []);

	/**
	 * Enable auto-selection of single available dates.
	 *
	 * By default, auto-selection is disabled. Use this filter to enable it.
	 * Can be filtered using Gravity Forms' applyFilters.
	 *
	 * @param {boolean} autoSelectEnabled - Whether auto-selection is enabled (default: false)
	 * @param {Object}  context           - Context object containing service and field information
	 * @param {Object}  context.service   - The current service object
	 *
	 * @example Enable auto-selection for all services
	 * <code>
	 * window.gform.addFilter('gpb_auto_select_single_date', function(autoSelectEnabled, context) {
	 *     return true;
	 * });
	 * </code>
	 *
	 * @example Enable auto-selection for specific service
	 * <code>
	 * window.gform.addFilter('gpb_auto_select_single_date', function(autoSelectEnabled, context) {
	 *     if (context.service?.id === 123) {
	 *         return true;
	 *     }
	 *     return autoSelectEnabled;
	 * });
	 * </code>
	 */
	const autoSelectEnabled = window.gform?.applyFilters
		? window.gform.applyFilters(
				'gpb_auto_select_single_date',
				false, // Default: disabled
				{ service }
			)
		: false;

	// Auto-select single available date
	useEffect(() => {
		// Skip if auto-selection is disabled
		if (!autoSelectEnabled) {
			return;
		}

		// Skip if still loading, no data, already have selection, or in entry edit mode
		if (
			loading ||
			!monthData ||
			Object.keys(monthData).length === 0 ||
			selectedDate ||
			selectedRange ||
			isEntryEdit
		) {
			return;
		}

		// Get available dates in current month
		const availableDates = Object.entries(monthData)
			.filter(([, dayData]) => dayData.available === true)
			.filter(([dateKey]) => {
				const date = new Date(dateKey);
				return date >= today;
			});

		// Auto-select if exactly one available date.
		if (availableDates.length === 1) {
			const [dateKey] = availableDates[0];
			const autoSelectDate = new Date(dateKey);

			setSelectedDate(autoSelectDate);
		}
	}, [
		autoSelectEnabled,
		loading,
		monthData,
		selectedDate,
		selectedRange,
		isEntryEdit,
		today,
		setSelectedDate,
	]);

	// Show skeleton while locale is loading
	if (localeLoading || !locale) {
		return (
			<div className="gpb-booking-time-picker__calendar">
				<CalendarSkeleton />
			</div>
		);
	}

	/**
	 * Get the week start day for the calendar.
	 *
	 * Defaults to WordPress setting from Settings » General.
	 *
	 * This filter replaces the deprecated `gp_bookings_calendar_week_starts_on` filter,
	 * which is still supported for backwards compatibility.
	 *
	 * @param {number} weekStartsOn    - Day of week (0 = Sunday, 1 = Monday, etc.)
	 * @param {Object} context         - Context object containing service and field information
	 * @param {Object} context.service - The current service object
	 *
	 * @example Force calendar to always start on Monday
	 * <code>
	 * window.gform.addFilter('gpb_calendar_week_starts_on', function(weekStartsOn, context) {
	 *     return 1; // Monday
	 * });
	 * </code>
	 *
	 * @example Start on Monday for specific service
	 * <code>
	 * window.gform.addFilter('gpb_calendar_week_starts_on', function(weekStartsOn, context) {
	 *     if (context.service?.id === 123) {
	 *         return 1; // Monday
	 *     }
	 *     return weekStartsOn;
	 * });
	 * </code>
	 */
	let weekStartsOn = getSettings().l10n?.startOfWeek ?? 0;
	if (window.gform?.applyFilters) {
		weekStartsOn = window.gform.applyFilters(
			'gp_bookings_calendar_week_starts_on',
			weekStartsOn,
			{ service }
		);
		weekStartsOn = window.gform.applyFilters(
			'gpb_calendar_week_starts_on',
			weekStartsOn,
			{ service }
		);
	}

	// Custom handler for fixed-range selection (when min === max)
	const handleFixedRangeClick: DayEventHandler<React.MouseEvent> = (
		day,
		modifiers
	) => {
		if (modifiers.selected) {
			// Clear selection if clicking on already selected range
			setSelectedRange(undefined);
			return;
		}

		// Find a contiguous block of available dates that includes the clicked day
		const range = findAvailableRange(day, minDays);

		if (range) {
			setSelectedRange(range);
		}
	};

	const getFixedRangeEndDate = (startDate: Date, requiredDays: number) => {
		const offsetDays = service?.treatAsNights
			? requiredDays
			: requiredDays - 1;
		return addDays(startDate, offsetDays);
	};

	const getFixedRangeStartDate = (endDate: Date, requiredDays: number) => {
		const offsetDays = service?.treatAsNights
			? requiredDays
			: requiredDays - 1;
		return addDays(endDate, -offsetDays);
	};

	// Find a contiguous block of available dates around the clicked day
	const findAvailableRange = (
		clickedDay: Date,
		requiredDays: number
	): DateRange | null => {
		// Helper to check if a date is available
		const isDateAvailable = (date: Date): boolean => {
			const dateKey = format(date, 'yyyy-MM-dd');
			const dayData = monthData[dateKey];

			// If no data, assume unavailable
			if (!dayData) {
				return false;
			}

			// Check if date is in the past
			if (date < today) {
				return false;
			}

			return dayData.available === true;
		};

		// First, try to find a range starting from the clicked day
		let startDate = clickedDay;
		let endDate = getFixedRangeEndDate(startDate, requiredDays);

		// Check if all days from clicked day forward are available
		let allAvailable = true;
		for (let i = 0; i < requiredDays; i++) {
			const checkDate = addDays(startDate, i);
			if (!isDateAvailable(checkDate)) {
				allAvailable = false;
				break;
			}
		}

		if (allAvailable) {
			return { from: startDate, to: endDate };
		}

		// If forward doesn't work, try to find a contiguous block that includes the clicked day
		// by searching backwards and forwards to find the best fit

		// Find the start of the available block containing clicked day
		let blockStart = clickedDay;
		while (isDateAvailable(addDays(blockStart, -1))) {
			blockStart = addDays(blockStart, -1);
		}

		// Find the end of the available block containing clicked day
		let blockEnd = clickedDay;
		while (isDateAvailable(addDays(blockEnd, 1))) {
			blockEnd = addDays(blockEnd, 1);
		}

		// Calculate how many days are in this contiguous block
		const blockDays =
			Math.floor(
				(blockEnd.getTime() - blockStart.getTime()) /
					(1000 * 60 * 60 * 24)
			) + 1;

		// If the block is too small, return null
		if (blockDays < requiredDays) {
			return null;
		}

		// Try to center the range around the clicked day within the available block
		const daysBeforeClick = Math.floor(
			(clickedDay.getTime() - blockStart.getTime()) /
				(1000 * 60 * 60 * 24)
		);
		const daysAfterClick = Math.floor(
			(blockEnd.getTime() - clickedDay.getTime()) / (1000 * 60 * 60 * 24)
		);

		// Calculate ideal start position (trying to center around clicked day)
		const idealDaysBefore = Math.floor((requiredDays - 1) / 2);
		const idealDaysAfter = requiredDays - 1 - idealDaysBefore;

		if (
			daysBeforeClick >= idealDaysBefore &&
			daysAfterClick >= idealDaysAfter
		) {
			// Can center around clicked day
			startDate = addDays(clickedDay, -idealDaysBefore);
			endDate = getFixedRangeEndDate(startDate, requiredDays);
		} else if (daysBeforeClick < idealDaysBefore) {
			// Not enough days before, align to start of block
			startDate = blockStart;
			endDate = getFixedRangeEndDate(blockStart, requiredDays);
		} else {
			// Not enough days after, align to end of block
			endDate = blockEnd;
			startDate = getFixedRangeStartDate(blockEnd, requiredDays);
		}

		return { from: startDate, to: endDate };
	};

	// Create modifiers for original booking dates
	const originalModifiers = originalRange?.from
		? {
				original_booking: (date: Date) => {
					if (!originalRange?.from) {
						return false;
					}

					// Check if date matches original start date
					if (
						date.getFullYear() ===
							originalRange.from.getFullYear() &&
						date.getMonth() === originalRange.from.getMonth() &&
						date.getDate() === originalRange.from.getDate()
					) {
						return true;
					}

					// Check if date matches original end date (if different from start)
					if (
						originalRange.to &&
						originalRange.from.getTime() !==
							originalRange.to.getTime()
					) {
						if (
							date.getFullYear() ===
								originalRange.to.getFullYear() &&
							date.getMonth() === originalRange.to.getMonth() &&
							date.getDate() === originalRange.to.getDate()
						) {
							return true;
						}
					}

					return false;
				},
			}
		: {};

	// Availability status modifiers for custom day styling hooks.
	const availabilityStatusModifiers = {
		availability_available: (date: Date) => {
			const dateKey = format(date, 'yyyy-MM-dd');
			return monthData[dateKey]?.status === 'available';
		},
		availability_unavailable: (date: Date) => {
			const dateKey = format(date, 'yyyy-MM-dd');
			return monthData[dateKey]?.status === 'unavailable';
		},
		availability_outside: (date: Date) => {
			const dateKey = format(date, 'yyyy-MM-dd');
			return monthData[dateKey]?.status === 'no_availability';
		},
		availability_booked: (date: Date) => {
			const dateKey = format(date, 'yyyy-MM-dd');
			return monthData[dateKey]?.status === 'booked';
		},
	};

	const isDateInCurrentSelection = (date: Date): boolean => {
		if (!selectedRange?.from) {
			return false;
		}

		const dateKey = format(date, 'yyyy-MM-dd');
		const fromKey = format(selectedRange.from, 'yyyy-MM-dd');

		if (!selectedRange.to) {
			return dateKey === fromKey;
		}

		const toKey = format(selectedRange.to, 'yyyy-MM-dd');
		const [startKey, endKey] =
			fromKey <= toKey ? [fromKey, toKey] : [toKey, fromKey];

		return dateKey >= startKey && dateKey <= endKey;
	};

	const isDateInOriginalSelection = (date: Date): boolean => {
		if (!originalRange?.from) {
			return false;
		}

		const dateKey = format(date, 'yyyy-MM-dd');
		const fromKey = format(originalRange.from, 'yyyy-MM-dd');

		if (!originalRange.to) {
			return dateKey === fromKey;
		}

		const toKey = format(originalRange.to, 'yyyy-MM-dd');
		const [startKey, endKey] =
			fromKey <= toKey ? [fromKey, toKey] : [toKey, fromKey];

		return dateKey >= startKey && dateKey <= endKey;
	};

	const commonProps: DayPickerProps = {
		month: currentMonth,
		captionLayout: 'dropdown' as const,
		onMonthChange: onUserMonthChange,
		today,
		fromYear: today.getFullYear(),
		toYear: today.getFullYear() + 2,
		weekStartsOn,
		locale,
		modifiers: {
			...originalModifiers,
			...availabilityStatusModifiers,
		},
		modifiersClassNames: {
			original_booking: 'original_booking',
			availability_available: 'gpb-day--available',
			availability_unavailable: 'gpb-day--unavailable',
			availability_outside: 'gpb-day--outside-availability',
			availability_booked: 'gpb-day--booked',
		},
		disabled: isEntryEdit
			? [] // In entry edit mode, don't disable any dates
			: [
					{ before: today },
					(date: Date) => {
						const dateKey = format(date, 'yyyy-MM-dd');
						const dayData = monthData[dateKey];

						// If no month data is available (e.g., during loading), don't disable
						// This prevents clearing selections when month data is being fetched
						if (!dayData) {
							return false;
						}

						// Never disable the currently selected date(s) while rescheduling.
						// This keeps the active range clickable even if availability rules
						// would otherwise mark those days as unavailable.
						if (isDateInCurrentSelection(date)) {
							return false;
						}

						// During reschedule/edit flows we exclude the current booking
						// from availability queries. Keep original booked dates enabled
						// in the UI as well so users can select overlapping/new ranges.
						if (
							excludeBookingId &&
							isDateInOriginalSelection(date)
						) {
							return false;
						}

						if (isDateRange) {
							// Utilize allowedStart/allowedEnd for more complicated rules like nightly.
							if (
								!selectedRange?.from &&
								typeof dayData?.allowedStart !== 'undefined'
							) {
								return !dayData?.allowedStart;
							}

							if (
								selectedRange?.from &&
								typeof dayData?.allowedStart !== 'undefined' &&
								typeof dayData?.allowedEnd !== 'undefined'
							) {
								const currentDate = date.getTime();
								const startDate = selectedRange.from.getTime();

								if (currentDate < startDate) {
									// For dates before start: check allowedStart (can be a new start date?)
									return !dayData?.allowedStart;
								} else if (currentDate > startDate) {
									// For dates after start: check allowedEnd (can be an end date?)
									return !dayData?.allowedEnd;
								}
								// For the start date itself, don't disable it (it's already selected)
							}
						}

						return !dayData?.available;
					},
				],
		components: {
			Month: (
				props: {
					calendarMonth: CalendarMonth;
					displayIndex: number;
				} & HTMLAttributes<HTMLDivElement>
			) => {
				const { calendarMonth, displayIndex, className, ...divProps } =
					props;

				return (
					<div
						{...divProps}
						className={classnames(
							className,
							loading ? 'loading' : ''
						)}
						{...(loading ? { inert: 'inert' } : {})}
					>
						{loading && (
							<div className="gpb-booking-time-picker__calendar-loading">
								<div className="gpb-booking-time-picker__calendar-loading-text">
									{__(
										'Loading available dates…',
										'gp-bookings'
									)}
								</div>
							</div>
						)}

						{props.children}
					</div>
				);
			},
		},
	};

	const DAY_IN_MINUTES = 1440;

	let minDays = 1;
	let maxDays: number | undefined = 1;
	let mode: Mode = 'single';

	if (service && service.blockSize >= DAY_IN_MINUTES) {
		// Calculate min/max days based on service settings
		// If minDuration is set, use that. Otherwise, use blockSize as minimum.
		minDays = Math.ceil(
			service.minDuration
				? service.minDuration / DAY_IN_MINUTES
				: service.blockSize / DAY_IN_MINUTES
		);

		// If maxDuration is set, use that. Otherwise, if flexibleDuration is false, use minDays as maximum.
		maxDays = service.maxDuration
			? Math.ceil(service.maxDuration / DAY_IN_MINUTES)
			: service.flexibleDuration
				? undefined
				: minDays;

		// Enable range selection if flexibleDuration is true
		if (service.flexibleDuration) {
			mode = 'range';
		}
	}

	// Detect fixed-range mode (when min === max)
	const isFixedRange = minDays === maxDays && minDays > 1;

	return (
		<div className="gpb-booking-time-picker__calendar">
			{mode === 'range' ? (
				isFixedRange ? (
					// Use custom selection for fixed-range mode (min === max)
					<DayPicker
						{...commonProps}
						onDayClick={handleFixedRangeClick}
						modifiers={{
							...commonProps.modifiers,
							selected: selectedRange,
							range_start: selectedRange?.from,
							range_end: selectedRange?.to,
							range_middle: (date: Date) => {
								if (
									!selectedRange?.from ||
									!selectedRange?.to
								) {
									return false;
								}
								// Check if date is between start and end (excluding ends)
								return (
									date > selectedRange.from &&
									date < selectedRange.to
								);
							},
						}}
					/>
				) : (
					// Use built-in range mode for flexible ranges (min < max)
					<DayPicker
						mode="range"
						selected={selectedRange}
						onSelect={setSelectedRange}
						excludeDisabled={true} // Prevent selection of disabled days in range mode
						// React-day-picker's min/max counts the date span (difference between dates).
						// - Nightly mode: span naturally equals nights (Jan 1→3 = 2-day span = 2 nights)
						// - Day mode: span is 1 less than inclusive days (Jan 1→3 = 2-day span but 3 days booked)
						// So for day mode, subtract 1 to convert from inclusive day count to span count.
						min={service?.treatAsNights ? minDays : minDays - 1}
						max={
							service?.treatAsNights
								? maxDays
								: maxDays
									? maxDays - 1
									: undefined
						}
						{...commonProps}
					/>
				)
			) : (
				<DayPicker
					mode="single"
					selected={selectedDate}
					onSelect={setSelectedDate}
					{...commonProps}
				/>
			)}
			{mode === 'range' && selectedRange?.from && selectedRange?.to && (
				<div className="gpb-booking-time-picker__range-info">
					<p>
						{service?.treatAsNights ? (
							<>
								{__('Check-in:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.from)}
								<br />
								{__('Check-out:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.to)}
								<br />
								{__('Total nights:', 'gp-bookings')}{' '}
								{selectedCalendarDays}
							</>
						) : (
							<>
								{__('From:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.from)}
								<br />
								{__('To:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.to)}
								<br />
								{__('Total days:', 'gp-bookings')}{' '}
								{selectedCalendarDays + 1}
							</>
						)}
					</p>
				</div>
			)}
		</div>
	);
}
